import { CircularProgress, MenuItem, Stack, Typography, useTheme } from "@mui/material";
import { Button, IconButton, Menu, TooltipShortcut } from "@itk/mui-components";
import { AddEditProjectDialog } from "./components/add-edit-project-dialog";
import { GridColDef } from "@mui/x-data-grid";
import { useEffect, useState } from "react";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import EditIcon from "@mui/icons-material/EditOutlined";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import { RootState } from "src/store";
import { connect } from "react-redux";
import { projectListActions } from "./project-list-store";
import { applicationActions } from "../../components/application-store";
import { hasValue, hasValueAndNotEmpty } from "@itk/typescript-utils";
import { useNavigate } from "react-router";
import { ROUTES_URL } from "src/utils/constants";
import { Model_ProjectGet, Shared_ProjectVisibilitySettings } from "src/backend/openapi/v1";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import visibilityLock from "../../assets/images/visibility-lock.svg";
import { projectsPageK, useProjectsPageT } from "src/translation-hooks/projects-page";
import { genericTranslationsK, useGenericTranslationsT } from "src/translation-hooks/generic-translations";
import { useGetCustomerId } from "src/hooks";
import { DataGridWithIdentity, DekoxIcon } from "src/components";
import { DialogType } from "./components/add-edit-project-dialog/add-edit-project-dialog-types";
import { IDENTITY_SETTINGS_KEYS } from "src/utils/types/identity-settings-types";

const mapStateToProps = (state: RootState) => ({
  projectListState: state.pageReducer.projectListReducer.projectListState,
  shouldReload: state.pageReducer.projectListReducer.shouldReload,
  identitySettings: state.generalReducer.applicationReducer.identitySettings
});

const ownActions = {
  getProjectsRequest: projectListActions.getProjectsRequest,
  setWarningAction: applicationActions.setWarningAction,
  deleteProjectRequest: projectListActions.deleteProjectRequest,
  setShouldReload: projectListActions.setShouldReload,
  setIdentitySettingsRequest: applicationActions.setIdentitySettingsRequest
};

type Props = ReturnType<typeof mapStateToProps> & typeof ownActions;

const ProjectListBase = (props: Props) => {
  const { getProjectsRequest, projectListState, setWarningAction, deleteProjectRequest, setShouldReload, shouldReload, identitySettings, setIdentitySettingsRequest } = props;

  const projectsPageT = useProjectsPageT();
  const genericTranslationsT = useGenericTranslationsT();
  const { customerId } = useGetCustomerId();
  const theme = useTheme();
  const navigate = useNavigate();

  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);
  const [selectedProject, setSelectedProject] = useState<Model_ProjectGet>();

  const [addEditProjectDialogState, setAddEditProjectDialogState] = useState<{ isOpen: boolean; projectId?: string; variant: DialogType }>({ isOpen: false, variant: "create" });

  useEffect(() => {
    if (hasValueAndNotEmpty(customerId) && projectListState.loading === "not-started") {
      getProjectsRequest(customerId);
    }
  }, [customerId, projectListState.loading, getProjectsRequest]);

  useEffect(() => {
    if (hasValueAndNotEmpty(customerId) && shouldReload) {
      setShouldReload(false);
      getProjectsRequest(customerId);
    }
  }, [shouldReload, customerId]);

  const handleEdit = () => {
    if (!selectedProject?.projectId) return;
    setAddEditProjectDialogState({ isOpen: true, projectId: selectedProject.projectId, variant: "edit" });
  };

  const handleDelete = () => {
    if (!selectedProject || !hasValue(customerId)) return;
    setWarningAction({
      confirm: () => {
        deleteProjectRequest(selectedProject.projectId, customerId);
      },
      dialogProps: {
        message: projectsPageT.areYouSureDeleteProject,
        confirmButtonTranslation: {
          t: genericTranslationsT,
          k: genericTranslationsK,
          field: "delete"
        },
        messageIcon: <DeleteIcon />
      },
      variant: "confirmCancel"
    });
  };

  // TODO
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleRowClick = (projectId: string) => {
    navigate(ROUTES_URL.CONFIGURATOR.build(projectId));
  };

  const handleCreateProject = () => {
    setAddEditProjectDialogState({ isOpen: true, variant: "create" });
  };

  const columns: GridColDef<Model_ProjectGet>[] = [
    {
      field: "description",
      headerName: projectsPageT.projectName,
      flex: 1,
      renderCell: params => (
        <Typography overflow="hidden" textOverflow="ellipsis">
          {params.row.description}
        </Typography>
      )
    },
    {
      field: "visibility",
      headerName: projectsPageT.visibility,
      flex: 1,
      renderCell: params => (
        <Stack direction="row" gap={1}>
          {params.row.projectVisibilitySettingId === Shared_ProjectVisibilitySettings.Public && (
            <>
              <VisibilityOutlinedIcon />
              <Typography variant="subtitle1">{projectsPageT.public}</Typography>
            </>
          )}
          {params.row.projectVisibilitySettingId === Shared_ProjectVisibilitySettings.Private && (
            <>
              <VisibilityOffOutlinedIcon fontSize="medium" />
              <Typography variant="subtitle1">{projectsPageT.private}</Typography>
            </>
          )}
          {params.row.projectVisibilitySettingId === Shared_ProjectVisibilitySettings.ContactPerson && (
            <>
              <img src={visibilityLock} alt="visiblity lock" height={24} width={24} />
              <Typography variant="subtitle1">
                {params.row.projectVisibilityContactPersonsCount} {params.row.projectVisibilityContactPersonsCount > 1 ? projectsPageT.users : projectsPageT.user}
              </Typography>
            </>
          )}
        </Stack>
      )
    },
    ...(projectListState.loading === "has-loaded" && projectListState.data?.[0]?.attributes?.length > 0
      ? projectListState.data[0].attributes.map((attribute, index): GridColDef => {
          return {
            field: `attribute ${index}`,
            headerName: attribute.description,
            flex: 1,
            renderCell: params => {
              const att = (params.row as Model_ProjectGet)?.attributes?.find?.(x => attribute?.attributeId === x?.attributeId);

              return (
                <Typography overflow="hidden" textOverflow="ellipsis">
                  {att?.valueDescription ?? "-"}
                </Typography>
              );
            }
          };
        })
      : []),
    {
      field: "actions",
      headerName: "",
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: params => (
        <Stack direction="row" justifyContent="flex-end">
          <IconButton
            size="small"
            k={genericTranslationsK.edit}
            onClick={e => {
              setMenuAnchorEl(e.currentTarget);
              setSelectedProject(params.row);
            }}
          >
            <MoreVertIcon />
          </IconButton>
          <IconButton size="small" k={genericTranslationsK.delete} onClick={() => handleRowClick(params.row.projectId)}>
            <DekoxIcon icon="arrow-right" />
          </IconButton>

          <Menu
            anchorEl={menuAnchorEl}
            open={hasValue(menuAnchorEl)}
            onClose={() => {
              setMenuAnchorEl(null);
            }}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            sx={{ "& .MuiPaper-root": { boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.1)" } }}
          >
            <MenuItem sx={{ gap: 1 }} onClick={() => handleEdit()}>
              <EditIcon color="action" />
              <Typography>{projectsPageT.editProjectDetails}</Typography>
            </MenuItem>
            <MenuItem sx={{ gap: 1 }} onClick={() => handleDelete()}>
              <DeleteIcon color="action" />
              <Typography>{projectsPageT.deleteProject}</Typography>
            </MenuItem>
          </Menu>
        </Stack>
      )
    }
  ];

  return (
    <Stack flex={1} px={5} py={3} gap={2.5} overflow="auto">
      <Stack direction="row" justifyContent="space-between" alignItems="center" width="100%">
        <Typography variant="h2" color={theme.palette.customColors.darkGrey}>
          {projectsPageT.projects}
        </Typography>
        <TooltipShortcut shortcut={{ action: handleCreateProject, shortCutKey: "Insert" }}>
          <Button t={projectsPageT} k={projectsPageK} field="newProject" variant="contained" startIcon={<DekoxIcon icon="add" />} onClick={handleCreateProject} />
        </TooltipShortcut>
      </Stack>

      <Stack flex={1} overflow="auto">
        {projectListState.loading === "is-loading" && (
          <Stack flex={1} justifyContent="center" alignItems="center">
            <CircularProgress />
          </Stack>
        )}
        {projectListState.loading === "has-loaded" && (
          <DataGridWithIdentity
            dataGridSettings={identitySettings[IDENTITY_SETTINGS_KEYS.PROJECT_LIST_DATA_GRID]}
            handleSaveSettings={value => setIdentitySettingsRequest(IDENTITY_SETTINGS_KEYS.PROJECT_LIST_DATA_GRID, JSON.stringify(value))}
            rows={projectListState.data}
            columns={columns}
            disableColumnMenu
            getRowId={data => data.projectId}
            onRowDoubleClick={params => handleRowClick(params.row.projectId)}
            onCellKeyDown={(rowEvent, e) => {
              if (e.key === "Enter" && rowEvent.colDef.field !== "actions") handleRowClick(rowEvent.row.projectId);
            }}
            hideFooter
            sx={{
              fontWeight: "normal",
              overflow: "auto",
              color: theme.palette.customColors.darkGrey,
              background: theme.palette.customColors.white,
              borderRadius: 1,
              "& .MuiDataGrid-columnHeaderTitle": {
                fontWeight: theme.typography.fontWeightBold,
                color: theme.palette.customColors.darkGrey
              },
              "& .MuiDataGrid-virtualScroller": {
                overflowX: "hidden"
              }
            }}
          />
        )}
      </Stack>

      {addEditProjectDialogState.isOpen && (
        <AddEditProjectDialog
          projectId={addEditProjectDialogState.projectId}
          setOpen={value => setAddEditProjectDialogState({ isOpen: value, variant: "create" })}
          variant={addEditProjectDialogState.variant}
        />
      )}
    </Stack>
  );
};

export const ProjectList = connect(mapStateToProps, ownActions)(ProjectListBase);
